import { useEffect, useState, type RefObject, type UIEventHandler } from "react";
import { Search, PinOff } from "lucide-react";
import { motion, MotionConfig } from "motion/react";
import { RouteListItem, sortByPinned, type Route, type VolumeUnit } from "../../../vendor/prism-next/map/logistics/index.js";
import type { LogisticsMapViewHandle } from "@/features/map/logistics-map-view";
import { usePolledRef } from "@/features/map/use-polled-ref";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/** Start fetching the next page this far (px) before the viewport's bottom,
 *  so more rows are ready before the user hits a hard end-of-list stop. */
const LOAD_MORE_THRESHOLD_PX = 150;

export interface RouteSidebarProps {
  routes: Route[];
  hoveredIndex: number | null;
  mapRef: RefObject<LogisticsMapViewHandle | null>;
  /** Environment volume-capacity unit — forwarded to each RouteListItem. */
  capacityUnit: VolumeUnit;
  /** Pinned routes by path_id in pin order — prism-next's authoritative pin set
   *  (the map's `onPinnedChange`), so a Pin here, the deck's ✕, and data resets all
   *  agree. Pinning docks a detail card in prism-next's built-in pinned deck. */
  pinnedPathIds: readonly string[];
  /** Whether the server has more shipments for this day beyond what's loaded. */
  hasMore: boolean;
  /** True while a "load more" page is in flight (distinct from the initial load). */
  loadingMore: boolean;
  /** Fetch the next page (a no-op if one's already in flight or there's no more). */
  onLoadMore: () => void;
}

function matchesQuery(r: Route, query: string): boolean {
  if (!query) return true;
  const hay = [r.ship_ref, r.status, r.truck_class, r.path_id, ...r.stops.map((s) => s.name), ...r.stops.flatMap((s) => s.order_refs ?? [])]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(query);
}

/** Left-hand shipment list — search / hide / pin / toggle-all onto prism-next's
 *  <RouteListItem>. Visibility + pin are driven through RouteOverlay; the pin SET
 *  is owned by the overlay (`onPinnedChange`, passed in as `pinnedPathIds`) and the
 *  pinned-first ordering uses prism-next's `sortByPinned`. Pinning docks a detail
 *  card in prism-next's built-in pinned deck. */
export function RouteSidebar({ routes, hoveredIndex, mapRef, capacityUnit, pinnedPathIds, hasMore, loadingMore, onLoadMore }: RouteSidebarProps) {
  const [query, setQuery] = useState("");
  const [manualHidden, setManualHidden] = useState<Set<number>>(new Set());
  const overlay = usePolledRef(mapRef, (h) => h.routes);
  const pinned = new Set(pinnedPathIds);

  // New data load (date/site change) — drop per-load UI state. Pin state resets
  // itself: the overlay drops stale pins on setRoutes and fires onPinnedChange.
  useEffect(() => {
    setManualHidden(new Set());
    setQuery("");
  }, [routes]);

  const order = sortByPinned(
    routes.map((_, i) => i).filter((i) => matchesQuery(routes[i], query)),
    pinnedPathIds,
    (i) => routes[i].path_id,
  );
  const orderSet = new Set(order);

  // Keep the map overlay's per-route visibility in sync with search + hide.
  useEffect(() => {
    if (!overlay) return;
    routes.forEach((_, i) => overlay.setVisible(i, orderSet.has(i) && !manualHidden.has(i)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay, routes, query, manualHidden]);

  const shown = order.filter((i) => !manualHidden.has(i)).length;
  const anyVisibleMatching = order.some((i) => !manualHidden.has(i));

  const setHidden = (i: number, hidden: boolean): void => {
    setManualHidden((prev) => {
      const next = new Set(prev);
      if (hidden) next.add(i);
      else next.delete(i);
      return next;
    });
  };

  // Pin toggles just mutate the overlay; the overlay's onPinnedChange then updates
  // `pinnedPathIds` (via the page), re-rendering this list and driving the deck.
  const setPinnedRow = (i: number, pin: boolean): void => {
    if (pin) {
      overlay?.pin(i);
      overlay?.focusRoute(i);
    } else {
      overlay?.unpin(i);
    }
  };

  const toggleAll = (): void => {
    setManualHidden(anyVisibleMatching ? new Set(order) : new Set());
  };

  const handleViewportScroll: UIEventHandler<HTMLDivElement> = (e) => {
    if (!hasMore || loadingMore) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX) {
      onLoadMore();
    }
  };

  return (
    <div className="flex h-full min-h-0 w-80 flex-col gap-2 border-r p-3">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={toggleAll} disabled={!order.length}>
          {anyVisibleMatching ? "Hide all" : "Show all"}
        </Button>
        <span className="text-xs text-muted-foreground">
          {shown}/{routes.length} shown
          {manualHidden.size ? ` · ${manualHidden.size} hidden` : ""}
        </span>
        {pinnedPathIds.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Unpin all ${pinnedPathIds.length} pinned`}
                className="ml-auto text-muted-foreground hover:text-foreground"
                onClick={() => routes.forEach((r, i) => pinned.has(r.path_id) && overlay?.unpin(i))}
              >
                <PinOff />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Unpin all</TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className="relative">
        <Search className="absolute top-1/2 left-2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value.trim().toLowerCase())}
          placeholder="Search shipments…"
          className="pl-8"
        />
      </div>
      <ScrollArea className="min-h-0 flex-1" onViewportScroll={handleViewportScroll}>
        <MotionConfig reducedMotion="user">
        <div className="flex flex-col gap-1 pr-2">
          {!routes.length && <p className="p-4 text-center text-sm text-muted-foreground">No shipments loaded.</p>}
          {routes.length > 0 && !order.length && (
            <p className="p-4 text-center text-sm text-muted-foreground">No matches.</p>
          )}
          {order.map((i) => {
            const r = routes[i];
            const visible = !manualHidden.has(i);
            const isPinned = pinned.has(r.path_id);
            const color = overlay?.routeColor(i) ?? "var(--muted-foreground)";
            return (
              // `layout="position"` FLIP-animates the row to its new slot when pinning
              // reshuffles the order (stable key → same DOM node moves). MotionConfig
              // (below) honours prefers-reduced-motion.
              <motion.div key={r.path_id} layout="position" transition={{ duration: 0.2, ease: "easeOut" }}>
                <RouteListItem
                  route={r}
                  color={color}
                  visible={visible}
                  pinned={isPinned}
                  capacityUnit={capacityUnit}
                  onHoverChange={(hovered) => (hovered ? overlay?.hoverRoute(i) : overlay?.clearHover())}
                  onVisibleChange={(next) => setHidden(i, !next)}
                  onPinnedChange={(next) => setPinnedRow(i, next)}
                  className={hoveredIndex === i ? "bg-muted/50" : undefined}
                />
              </motion.div>
            );
          })}
          {loadingMore && (
            <div className="flex items-center justify-center gap-2 p-3 text-xs text-muted-foreground">
              <Spinner className="size-3.5" />
              Loading more…
            </div>
          )}
        </div>
        </MotionConfig>
      </ScrollArea>
    </div>
  );
}
